import i18n from 'i18next';

/**
 * Date utility functions for timezone-correct display.
 *
 * The database stores all datetimes as UTC. When displaying in
 * datetime-local inputs, we must convert UTC → local time so
 * the user sees (and edits) their own timezone, not UTC.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True for a date-only value from the API ("2026-10-22"): a day without time.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isDateOnly(value) {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

/**
 * Parses a datetime from the API as UTC.
 *
 * The backend serializes DATETIME columns without an offset
 * ("2026-10-25T20:00:00"), and new Date() would read that as LOCAL
 * time, shifting it 6 hours in El Salvador. Values that already carry
 * "Z" or an offset are parsed as-is; Date objects pass through.
 * Date-only values ("2026-10-22", DATE columns) are calendar days, not
 * instants: they become local midnight of that day. new Date('YYYY-MM-DD')
 * reads them as UTC midnight, which is the day before in El Salvador.
 *
 * @param {string|Date|null} value - datetime or date from the API
 * @returns {Date|null} the instant, or null if falsy/invalid
 */
export function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const dateOnly = DATE_ONLY.exec(String(value).trim());
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  let s = String(value).trim().replace(' ', 'T');
  if (/T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Local calendar day (YYYY-MM-DD) of an API datetime.
 *
 * @param {string|Date|null} value - datetime from the API
 * @returns {string} "YYYY-MM-DD" in local time, or '' if falsy
 */
export function toLocalDateKey(value) {
  const d = parseApiDate(value);
  if (!d) return '';
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Today's local calendar day (YYYY-MM-DD), optionally N days ahead, for date-only form defaults.
 *
 * new Date().toISOString().split('T')[0] is the UTC day: after 18:00 in El Salvador (UTC-6)
 * it is already tomorrow, so forms proposed the next day (#350).
 *
 * @param {number} days - days to add to today (may be negative)
 * @returns {string} "YYYY-MM-DD" in local time
 */
export function localToday(days = 0) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalDateKey(d);
}

/**
 * Converts a UTC datetime string to the YYYY-MM-DDTHH:MM format
 * that <input type="datetime-local"> expects, expressed in the
 * browser's LOCAL timezone.
 *
 * Without this, El Salvador users (GMT-6) would see times 6 hours
 * ahead of their actual local time.
 *
 * @param {string|null} utcString - ISO 8601 UTC string from the API
 * @returns {string} "YYYY-MM-DDTHH:MM" in local time, or '' if falsy
 */
export function toLocalDatetimeInput(utcString) {
  const d = parseApiDate(utcString);
  if (!d) return '';
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins  = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
}

/**
 * Converts a value from a <input type="datetime-local"> (which is
 * local time, no timezone suffix) to a UTC ISO string for the API.
 *
 * new Date("YYYY-MM-DDTHH:MM") is parsed as LOCAL time in modern
 * browsers, so .toISOString() correctly produces UTC.
 *
 * @param {string} localInputValue - value from datetime-local input
 * @returns {string|null} UTC ISO string, or null if falsy
 */
export function localInputToUtc(localInputValue) {
  if (!localInputValue) return null;
  return new Date(localInputValue).toISOString();
}

const DATE_STYLES = {
  date:     { day: '2-digit', month: '2-digit', year: 'numeric' },
  datetime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  time:     { hour: '2-digit', minute: '2-digit' },
};

/**
 * Locale for displaying dates in the app's current i18n language,
 * not the browser's. Spanish uses 'es' (DD/MM/YYYY, 24-hour clock,
 * same as the header clock); 'es-SV' would switch to "p. m.".
 *
 * @returns {string} BCP 47 locale for Intl
 */
export function getAppDateLocale() {
  const lng = i18n.resolvedLanguage || i18n.language || 'en';
  return lng.startsWith('es') ? 'es' : 'en-US';
}

/**
 * Formats a date for display in the app's language.
 * es: "22/10/2026", "22/10/2026, 23:17", "23:17"
 * en: "10/22/2026", "10/22/2026, 11:17 PM", "11:17 PM"
 *
 * Datetimes from the API are read with parseApiDate (no offset = UTC).
 * Date-only values ("2026-10-22", DATE columns) are calendar days, not
 * instants: they are shown as-is, without timezone conversion (read as
 * UTC midnight they would show the day before in El Salvador).
 *
 * @param {string|number|Date|null} value - date from the API or a Date
 * @param {'date'|'datetime'|'time'|Object} style - preset or Intl options
 * @returns {string} formatted date, or '' if falsy/invalid
 */
export function formatAppDate(value, style = 'date') {
  if (value === null || value === undefined || value === '') return '';
  const d = typeof value === 'number' ? new Date(value) : parseApiDate(value);
  if (!d || isNaN(d.getTime())) return '';
  const options = typeof style === 'string' ? DATE_STYLES[style] : style;
  return d.toLocaleString(getAppDateLocale(), options);
}
